/**
 * useMathCoachMessages hook
 * Manages sending image and text messages in a Math Coach session with optimistic UI.
 *
 * Based on the optimistic update pattern from useSubmitAnswer (MCP mutation pattern,
 * lines 104-166). Simplified for math coach: no pebbles, no demo limits, no serverless mode.
 *
 * AC coverage:
 *   AC-003.3 - Image appears immediately in chat (optimistic UI) + typing indicator follows
 *   AC-003.4 - Text follow-up appears immediately (optimistic UI)
 *   AC-003.7 - Failed send removes optimistic message and sets sendError
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { postMathCoachMessage } from '@/services/mathCoach';
import type { Message, SessionMessagesResponse } from '@/types/session';

export interface UseMathCoachMessagesReturn {
  /** Send a text follow-up message */
  sendTextMessage: (content: string) => void;
  /** Send an image message. previewUrl is a local blob URL for optimistic display. */
  sendImageMessage: (base64Data: string, mimeType: string, previewUrl: string) => void;
  /** True while a send is in progress */
  isSending: boolean;
  /** User-friendly error message (null when no error) */
  sendError: string | null;
  /** Dismiss the current send error */
  clearError: () => void;
}

/**
 * Maps API error codes to user-friendly messages for Math Coach context.
 */
function mapSendError(err: unknown): string {
  if (err !== null && typeof err === 'object') {
    const e = err as { code?: string; message?: string };
    if (e.code === 'NETWORK_ERROR') {
      return 'Upload failed — please check your connection and try again.';
    }
    if (e.code === 'UNAUTHORIZED') {
      return 'You must be logged in to send messages.';
    }
    if (e.code === 'BUSINESS_RULE_VIOLATION') {
      return 'This session has ended. You cannot send new messages.';
    }
    if (e.message) {
      return e.message;
    }
  }
  return 'Upload failed — please try again.';
}

/**
 * Hook for sending Math Coach messages with optimistic UI and rollback on failure.
 *
 * @param sessionId       - The active Math Coach session ID
 * @param notifyPollDelay - Callback from useSessionMessages; called after successful send
 *                          to start the appropriate poll delay (30s image, 10s text).
 */
export function useMathCoachMessages(
  sessionId: string,
  notifyPollDelay: (type: 'image' | 'text') => void
): UseMathCoachMessagesReturn {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // ---- Text message mutation ----
  const textMutation = useMutation<
    { decisionId: string; sessionId: string; timestamp: string },
    unknown,
    string,
    { previousMessages?: SessionMessagesResponse }
  >({
    mutationFn: async (content: string) => {
      if (!user) {
        throw { code: 'UNAUTHORIZED', message: 'You must be logged in to send messages.' };
      }
      return await postMathCoachMessage(sessionId, user.userId, content.trim(), 'text');
    },

    onMutate: async (content: string) => {
      // Cancel outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['session', sessionId, 'messages'] });

      const previousMessages = queryClient.getQueryData<SessionMessagesResponse>([
        'session',
        sessionId,
        'messages',
      ]);

      if (previousMessages) {
        const optimisticMessage: Message = {
          id: `temp-${Date.now()}`,
          role: 'parent',
          content: content.trim(),
          type: 'text',
          timestamp: new Date().toISOString(),
          decisionType: 'feedback',
        };

        queryClient.setQueryData<SessionMessagesResponse>(['session', sessionId, 'messages'], {
          ...previousMessages,
          messages: [...previousMessages.messages, optimisticMessage],
        });
      }

      return { previousMessages };
    },

    onSuccess: () => {
      notifyPollDelay('text');
      // Trigger refetch so polling picks up the tutor response
      queryClient.invalidateQueries({ queryKey: ['session', sessionId, 'messages'] });
    },

    onError: (_err, _content, context) => {
      // Rollback optimistic update (AC-003.7)
      if (context?.previousMessages) {
        queryClient.setQueryData(['session', sessionId, 'messages'], context.previousMessages);
      }
    },
  });

  // ---- Image message mutation ----
  const imageMutation = useMutation<
    { decisionId: string; sessionId: string; timestamp: string },
    unknown,
    { base64Data: string; mimeType: string; previewUrl: string },
    { previousMessages?: SessionMessagesResponse }
  >({
    mutationFn: async ({ base64Data, mimeType }) => {
      if (!user) {
        throw { code: 'UNAUTHORIZED', message: 'You must be logged in to send messages.' };
      }
      return await postMathCoachMessage(sessionId, user.userId, base64Data, 'image', mimeType);
    },

    onMutate: async ({ previewUrl, mimeType }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['session', sessionId, 'messages'] });

      const previousMessages = queryClient.getQueryData<SessionMessagesResponse>([
        'session',
        sessionId,
        'messages',
      ]);

      if (previousMessages) {
        // Optimistic image message uses local blob URL for immediate display (AC-003.3)
        const optimisticMessage: Message = {
          id: `temp-img-${Date.now()}`,
          role: 'parent',
          content: '',
          type: 'image',
          imageUrl: previewUrl,
          mimeType,
          timestamp: new Date().toISOString(),
          decisionType: 'feedback',
        };

        queryClient.setQueryData<SessionMessagesResponse>(['session', sessionId, 'messages'], {
          ...previousMessages,
          messages: [...previousMessages.messages, optimisticMessage],
        });
      }

      return { previousMessages };
    },

    onSuccess: () => {
      notifyPollDelay('image');
      // Trigger refetch so polling picks up tutor response and real S3 URL
      queryClient.invalidateQueries({ queryKey: ['session', sessionId, 'messages'] });
    },

    onError: (_err, _variables, context) => {
      // Rollback optimistic image (AC-003.7)
      if (context?.previousMessages) {
        queryClient.setQueryData(['session', sessionId, 'messages'], context.previousMessages);
      }
    },
  });

  const isSending = textMutation.isPending || imageMutation.isPending;

  // Derive user-friendly error from whichever mutation failed
  const rawError = textMutation.error ?? imageMutation.error;
  const sendError = rawError != null ? mapSendError(rawError) : null;

  const clearError = () => {
    textMutation.reset();
    imageMutation.reset();
  };

  const sendTextMessage = (content: string) => {
    if (!content.trim()) return;
    textMutation.mutate(content);
  };

  const sendImageMessage = (base64Data: string, mimeType: string, previewUrl: string) => {
    imageMutation.mutate({ base64Data, mimeType, previewUrl });
  };

  return {
    sendTextMessage,
    sendImageMessage,
    isSending,
    sendError,
    clearError,
  };
}
